import * as zod from 'zod';

import { getIntegrationTypes } from '@sb/integrations/loaders/steps';

import AnticipatePayloadVariables from './AnticipatePayload/Variables';
import CallSubroutineVariables from './CallSubroutine/Variables';
import CaptureVariables from './Capture/Variables';
import ClassifyVariables from './Classify/Variables';
import CodeBlockVariables from './CodeBlock/Variables';
import ExternalControlVariables from './ExternalControl/Variables';
import IfVariables from './If/Variables';
import LocateVariables from './Locate/Variables';
import LoopVariables from './Loop/Variables';
import LoopControlVariables from './LoopControl/Variables';
import MoveArmToVariables from './MoveArmTo/Variables';
import MoveArmToV2Variables from './MoveArmToV2/Variables';
import NetworkRequestVariables from './NetworkRequest/Variables';
import PressButtonVariables from './PressButton/Variables';
import RunLocateSkillVariables from './RunLocateSkill/Variables';
import SamplePositioningApproachVariables from './SamplePositioningApproach/Variables';
import SetEnvironmentVariableVariables from './SetEnvironmentVariable/Variables';
import SetIOVariables from './SetIO/Variables';
import type { StepKind } from './StepKind';
import TeleopVariables from './Teleop/Variables';
import TriggerFaultVariables from './TriggerFault/Variables';
import WaitVariables from './Wait/Variables';
import WaitForConfirmationVariables from './WaitForConfirmation/Variables';
import WaypointVariables from './Waypoint/Variables';

/**
 * An untagged union of routine step variable state.
 *
 * Any possible state data for a variable.
 */
export const RoutineStepVariables = zod.union([
  AnticipatePayloadVariables,
  CallSubroutineVariables,
  CaptureVariables,
  ClassifyVariables,
  ExternalControlVariables,
  LocateVariables,
  IfVariables,
  LoopVariables,
  LoopControlVariables,
  MoveArmToVariables,
  NetworkRequestVariables,
  PressButtonVariables,
  RunLocateSkillVariables,
  SamplePositioningApproachVariables,
  SetEnvironmentVariableVariables,
  SetIOVariables,
  TeleopVariables,
  WaitForConfirmationVariables,
  WaitVariables,
  TriggerFaultVariables,
  WaypointVariables,
  ...getIntegrationTypes().map(({ Variables }) => Variables),
]);

export type RoutineStepVariables = zod.infer<typeof RoutineStepVariables>;

function tag<V, S extends StepKind>(schema: zod.Schema<V>, stepKind: S) {
  return schema.and(
    zod.object({
      stepKind: zod.literal<S>(stepKind),
    }),
  );
}

/**
 * A tagged union of routine step variable state.
 *
 * Any possible state data for a variable, tagged with `{ kind: StepKind }`.
 */
export const TaggedRoutineStepVariables = zod.union([
  tag(AnticipatePayloadVariables, 'AnticipatePayload'),
  tag(CallSubroutineVariables, 'CallSubroutine'),
  tag(CaptureVariables, 'Capture'),
  tag(ClassifyVariables, 'Classify'),
  tag(CodeBlockVariables, 'CodeBlock'),
  tag(ExternalControlVariables, 'ExternalControl'),
  tag(LocateVariables, 'Locate'),
  tag(RunLocateSkillVariables, 'RunLocateSkill'),
  tag(IfVariables, 'If'),
  tag(LoopVariables, 'Loop'),
  tag(LoopControlVariables, 'LoopControl'),
  tag(MoveArmToVariables, 'MoveArmTo'),
  tag(MoveArmToV2Variables, 'MoveArmToV2'),
  tag(PressButtonVariables, 'PressButton'),
  tag(SamplePositioningApproachVariables, 'SamplePositioningApproach'),
  tag(SetEnvironmentVariableVariables, 'SetEnvironmentVariable'),
  tag(SetIOVariables, 'SetIO'),
  tag(TeleopVariables, 'Teleop'),
  tag(WaitForConfirmationVariables, 'WaitForConfirmation'),
  tag(TriggerFaultVariables, 'TriggerFault'),
  tag(WaitVariables, 'Wait'),
  tag(WaypointVariables, 'Waypoint'),
  zod.object({
    stepKind: zod.literal('NetworkRequest'),
    value: zod.any(),
  }),
  zod.object({
    stepKind: zod.literal('EnvironmentVariable'),
    value: zod.any(),
    name: zod.string(),
    isGlobal: zod.boolean().optional().default(false),
    persistent: zod.boolean().optional().default(false),
  }),
  zod.object({
    stepKind: zod.literal('RunLearnedModel'),
    value: zod.any(),
    name: zod.string(),
  }),
  ...getIntegrationTypes().map(({ stepKind, Variables }) =>
    tag(Variables, stepKind),
  ),
]);

export type TaggedRoutineStepVariables = zod.infer<
  typeof TaggedRoutineStepVariables
>;
export type AnyVariables = RoutineStepVariables;
